import { Node, mergeAttributes } from '@tiptap/core';
import { Mention } from '@tiptap/extension-mention';

// Reemplaza el `insertMention()` manual de ComponentTaskHoy.jsx -- mismo shape final
// (`<span class="mention-pill" contenteditable="false" data-uid data-name data-avatar>`), pero
// como nodo de TipTap. `parseHTML` acepta CUALQUIER `span[data-uid]`, con o sin
// `class="mention-pill"` -- los tests backend existentes (comments.test.js) ya guardan
// menciones sin esa clase, y `data-uid` (no la clase) es el contrato del que depende
// `_buildNotification` (CreateComment.js) para notificar. `renderHTML` siempre emite
// `data-uid` -- si dejara de hacerlo, las notificaciones por mención se rompen en silencio.
export const TaskCommentMention = Mention.extend({
    name: 'mention',
    addAttributes() {
        return {
            uid: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-uid'),
                renderHTML: (attrs) => (attrs.uid ? { 'data-uid': attrs.uid } : {}),
            },
            name: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-name') || (el.textContent || '').replace(/^@/, '').trim(),
                renderHTML: (attrs) => (attrs.name ? { 'data-name': attrs.name } : {}),
            },
            avatar: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-avatar'),
                renderHTML: (attrs) => (attrs.avatar ? { 'data-avatar': attrs.avatar } : {}),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-uid]' }];
    },
    renderHTML({ node, HTMLAttributes }) {
        const children = [];
        if (node.attrs.avatar) children.push(['img', { src: node.attrs.avatar }]);
        children.push(` ${node.attrs.name || ''}`);
        return ['span', mergeAttributes(HTMLAttributes, { class: 'mention-pill', contenteditable: 'false' }), ...children];
    },
    renderText({ node }) {
        return `@${node.attrs.name || ''}`;
    },
});

// Imagen/video/adjunto embebidos -- al CARGAR un comentario existente para editar, el HTML ya
// viene resuelto por el backend (`GetComments.js` -> `embeddedContent.js:resolve()`), con el
// wrapper de candado `<span class="cy-embed-media">...<span class="cy-embed-toggle">svg</span>
// </span>` alrededor de la imagen/video real. Estos nodos solo reconocen la etiqueta base
// (`img`/`video`/`a.file-tag`) -- el wrapper, al no tener ningún nodo/mark que lo reconozca, se
// "desenvuelve" solo durante el parseo (ProseMirror descarta el tag contenedor que no matchea
// ningún nodo del schema y sigue parseando sus hijos). Al EMITIR de vuelta el HTML (`getHTML()`)
// se genera siempre solo la etiqueta base, nunca el wrapper -- así el backend
// (`embeddedContent.js:serialize()`) siempre ve `<img>`/`<video>`/`<a class="file-tag">` limpios,
// sin importar si el comentario se cargó ya resuelto o recién se creó.
export const TaskCommentImage = Node.create({
    name: 'taskCommentImage',
    group: 'inline',
    inline: true,
    atom: true,
    addAttributes() {
        return {
            src: { default: null },
            alt: { default: '' },
        };
    },
    parseHTML() {
        return [{ tag: 'img[src]', getAttrs: (el) => ({ src: el.getAttribute('src'), alt: el.getAttribute('alt') || '' }) }];
    },
    renderHTML({ node, HTMLAttributes }) {
        return ['img', mergeAttributes(HTMLAttributes, { src: node.attrs.src, alt: node.attrs.alt, class: 'task-comment-embed-img', contenteditable: 'false' })];
    },
});

export const TaskCommentVideo = Node.create({
    name: 'taskCommentVideo',
    group: 'inline',
    inline: true,
    atom: true,
    addAttributes() {
        return { src: { default: null } };
    },
    parseHTML() {
        return [{ tag: 'video[src]', getAttrs: (el) => ({ src: el.getAttribute('src') }) }];
    },
    renderHTML({ node, HTMLAttributes }) {
        return ['video', mergeAttributes(HTMLAttributes, { src: node.attrs.src, controls: 'true', class: 'task-comment-embed-video', contenteditable: 'false' })];
    },
});

export const TaskCommentFileAttachment = Node.create({
    name: 'taskCommentFile',
    group: 'inline',
    inline: true,
    atom: true,
    addAttributes() {
        return {
            href: { default: null },
            fileName: { default: '' },
        };
    },
    parseHTML() {
        // Prioridad alta: sin esto, el mark `Link` de StarterKit (`a[href]`, genérico) podría
        // ganarle la carrera de parseo a este nodo sobre el mismo `<a class="file-tag">`.
        return [{ tag: 'a.file-tag[href]', priority: 100, getAttrs: (el) => ({ href: el.getAttribute('href'), fileName: (el.textContent || '').trim() }) }];
    },
    renderHTML({ node, HTMLAttributes }) {
        return ['a', mergeAttributes(HTMLAttributes, { href: node.attrs.href, target: '_blank', class: 'file-tag', contenteditable: 'false' }), node.attrs.fileName || node.attrs.href || ''];
    },
});
